import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { productMasterAPI, productTypeMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditProductTypeMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCategory = (item) => ({
  catgCode: item.catgCode || item.CATGCODE || item.catgcode || "",
  catgName: item.catgName || item.CATGNAME || item.catgname || "",
});

const normalizeProductType = (item) => ({
  productTypeCode: item.productTypeCode || item.ProductTypeCode || item.PRODUCTTYPECODE || "",
  productTypeName: item.productTypeName || item.ProductTypeName || item.productTypeName || "",
  description: item.description || item.Description || "",
  catgCode: item.catgCode || item.CATGCODE || item.catgcode || "",
  isActive: item.isActive ?? item.IsActive ?? true,
  displayOrder: item.displayOrder ?? item.DisplayOrder ?? null,
});

const AddEditProductTypeMaster = () => {
  const navigate = useNavigate();
  const { productTypeCode } = useParams();
  const isEditMode = !!productTypeCode;

  const routePermissions = getRoutePermissions("/product-types");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [productType, setProductType] = useState({
    productTypeCode: "",
    productTypeName: "",
    description: "",
    catgCode: "",
    isActive: true,
    displayOrder: null,
  });

  const [dropdowns, setDropdowns] = useState({
    categories: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const categorySelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} product types.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/product-types"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadProductType();
    } else {
      loadNextProductTypeCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeCode, canSave]);

  // Initialize Select2 for Category dropdown
  useEffect(() => {
    if (dropdowns.categories.length === 0) {
      return;
    }

    if (categorySelectRef.current) {
      const $categorySelect = $(categorySelectRef.current);
      if ($categorySelect.data("select2")) {
        $categorySelect.select2("destroy");
      }
      $categorySelect.select2({
        width: "100%",
        placeholder: "Select Category",
        allowClear: false,
      });
      $categorySelect.on("select2:select", function (e) {
        handleChange("catgCode", e.params.data.id);
      });
      if (productType.catgCode) {
        $categorySelect.val(productType.catgCode).trigger("change");
      }
    }

    return () => {
      if (categorySelectRef.current) {
        const $select = $(categorySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.categories]);

  // Update Select2 value when productType state changes
  useEffect(() => {
    if (categorySelectRef.current && dropdowns.categories.length > 0) {
      const $categorySelect = $(categorySelectRef.current);
      if ($categorySelect.data("select2")) {
        const currentVal = $categorySelect.val();
        if (currentVal !== (productType.catgCode || "")) {
          $categorySelect.val(productType.catgCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType.catgCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const catRes = await productMasterAPI.getCategories(true);

      setDropdowns({
        categories: (catRes?.data || []).map((c) => normalizeCategory(c)),
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadNextProductTypeCode = async () => {
    try {
      const response = await productTypeMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setProductType((prev) => ({ ...prev, productTypeCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next product type code:", e);
      setProductType((prev) => ({ ...prev, productTypeCode: "PT00001" }));
    }
  };

  const loadProductType = async () => {
    try {
      setLoading(true);
      const response = await productTypeMasterAPI.getByCode(productTypeCode);
      if (response && response.success && response.data) {
        setProductType(normalizeProductType(response.data));
      } else {
        navigate("/product-types");
      }
    } catch (e) {
      console.error("Error loading product type:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load component details", "error");
      }
      navigate("/product-types");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProductType((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!productType.productTypeCode?.trim()) {
        Swal.fire("Error", "Component ID is required", "error");
      return false;
    }
    if (!productType.productTypeName?.trim()) {
      Swal.fire("Error", "Component Name is required", "error");
      return false;
    }
    if (!productType.catgCode) {
      Swal.fire("Error", "Category is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await productTypeMasterAPI.update(
          productTypeCode,
          productType,
          createdBy
        );
      } else {
        response = await productTypeMasterAPI.create(productType, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Component saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/product-types");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save component",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving component:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save component. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/product-types"
            onClick={(e) => {
              e.preventDefault();
              navigate("/product-types");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Component Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Component" : "Add Component"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/product-types")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Category <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={categorySelectRef}
                  className="form-control select2-category"
                  required
                >
                  <option value="">Select Category</option>
                  {dropdowns.categories.map((c) => (
                    <option key={c.catgCode} value={c.catgCode}>
                      {c.catgName} ({c.catgCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component ID <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={productType.productTypeCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={productType.productTypeName}
                  onChange={(e) => handleChange("productTypeName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter component name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">Description</label>
                <TextField
                  value={productType.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter description"
                  multiline
                  rows={3}
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">Display Order</label>
                <TextField
                  type="number"
                  value={productType.displayOrder ?? ""}
                  onChange={(e) => handleChange("displayOrder", e.target.value ? parseInt(e.target.value) : null)}
                  fullWidth
                  size="small"
                  placeholder="Enter display order"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/product-types")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditProductTypeMaster;

